// authSubject.js -- the signed-in user id as the auth source last published it, readable synchronously.
// Single job: hold that one value and tell listeners when it changes. useAuth publishes it inside its auth callbacks, before React renders
// the new user, so a write that runs when an async read or send settles never acts for a user the auth
// source has already left (#1461 P1-D). undefined = never published (no auth source mounted, e.g. a test
// that renders the onboarding gate without useAuth).
import * as Sentry from './errorReporting';

let subject;
const listeners = new Set();
// Listeners run BEFORE the subject changes, with (prev, next), while authSubject() still reads prev: a writer
// that must act for the outgoing user (the onboarding gate stamping the phone lock at sign-out) does so
// synchronously at the auth boundary, not in a passive effect an external event can outrun (#1469).
export function publishAuthSubject(uid) {
  const next = uid || null;
  if (next !== subject) {
    for (const fn of [...listeners]) {
      try {
        fn(subject === undefined ? null : subject, next);
      } catch (err) {
        // Backstop only: a listener owns its own failure severity (the gate reports a missed lock stamp as an
        // exception itself). This catch keeps one bad listener from blocking the others or the subject change.
        Sentry.captureMessage('authSubject: listener failed', { level: 'warning', tags: { function: 'publishAuthSubject' }, extra: { errorName: err?.name, message: err?.message } });
      }
    }
  }
  subject = next;
}
export function addAuthSubjectListener(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function authSubject() { return subject; }
export function resetAuthSubjectForTests() { subject = undefined; listeners.clear(); }
